import { httpClient } from '@/core/api/http-client'
import type {
  CentralKitchenReport,
  CogsReport,
  CogsReportDto,
  StockReportRow,
  StockReportRowDto,
  UsageReport,
  UsageReportDto,
  WasteReport,
  WasteReportDto,
} from '@/domain/entities/inventory-report.entity'
import type { InventoryReportRepository } from '@/domain/repositories/inventory-report.repository'
import { unwrap } from '@/infrastructure/shared/unwrap'

export class InventoryReportHttpRepository implements InventoryReportRepository {
  async getStock(outletId: string, lowStockOnly?: boolean): Promise<StockReportRow[]> {
    const raw = await httpClient.get<unknown>('/reports/inventory/stock', {
      params: { outlet_id: outletId, low_stock: lowStockOnly ? 'true' : undefined },
    })
    const dtos = unwrap<StockReportRowDto[]>(raw)
    return (dtos ?? []).map((d) => ({
      ingredientId: d.ingredient_id,
      nama: d.name,
      stokQty: d.stock_qty,
      stokValue: d.stock_value,
      minStock: d.min_stock,
      rendah: d.low,
    }))
  }

  async getUsage(outletId: string, from?: string, to?: string): Promise<UsageReport> {
    const raw = await httpClient.get<unknown>('/reports/inventory/usage', {
      params: { outlet_id: outletId, from, to },
    })
    const dto = unwrap<UsageReportDto>(raw)
    return {
      from: dto.from,
      to: dto.to,
      rows: (dto.rows ?? []).map((r) => ({
        ingredientId: r.ingredient_id,
        nama: r.name,
        stokAwalQty: r.opening_qty ?? '0',
        stokAwalValue: r.opening_value ?? 0,
        masukQty: r.in_qty ?? '0',
        masukValue: r.in_value ?? 0,
        productionInQty: r.production_in_qty ?? '0',
        productionInValue: r.production_in_value ?? 0,
        productionOutQty: r.production_out_qty ?? '0',
        productionOutValue: r.production_out_value ?? 0,
        transferInQty: r.transfer_in_qty ?? '0',
        transferInValue: r.transfer_in_value ?? 0,
        transferOutQty: r.transfer_out_qty ?? '0',
        transferOutValue: r.transfer_out_value ?? 0,
        terjualQty: r.sold_qty ?? '0',
        terjualValue: r.sold_value ?? 0,
        wasteQty: r.waste_qty ?? '0',
        wasteValue: r.waste_value ?? 0,
        koreksiQty: r.adjustment_qty ?? '0',
        koreksiValue: r.adjustment_value ?? 0,
        stokAkhirQty: r.closing_qty ?? '0',
        stokAkhirValue: r.closing_value ?? 0,
        adaTaksiran: Boolean(r.has_estimated),
      })),
    }
  }

  async getWaste(outletId: string, from?: string, to?: string): Promise<WasteReport> {
    const raw = await httpClient.get<unknown>('/reports/inventory/waste', {
      params: { outlet_id: outletId, from, to },
    })
    const dto = unwrap<WasteReportDto>(raw)
    return {
      from: dto.from,
      to: dto.to,
      totalValue: dto.total_value,
      rows: (dto.rows ?? []).map((r) => ({
        ingredientId: r.ingredient_id,
        nama: r.name,
        qty: r.qty,
        value: r.value,
        kejadian: r.occurrences,
      })),
    }
  }

  async getCogs(outletId: string, from?: string, to?: string): Promise<CogsReport> {
    const raw = await httpClient.get<unknown>('/reports/inventory/cogs', {
      params: { outlet_id: outletId, from, to },
    })
    const dto = unwrap<CogsReportDto>(raw)
    return {
      from: dto.from,
      to: dto.to,
      totalOmzet: dto.total_revenue,
      totalHpp: dto.total_cogs,
      totalLabaKotor: dto.total_gross_profit,
      rows: (dto.rows ?? []).map((r) => ({
        outletId: r.outlet_id,
        outletName: r.outlet_name,
        productId: r.product_id,
        nama: r.name,
        qty: r.qty,
        omzet: r.revenue,
        hpp: r.cogs,
        labaKotor: r.gross_profit,
      })),
      variantRows: (dto.variant_rows ?? []).map((v) => ({
        outletId: v.outlet_id,
        outletName: v.outlet_name,
        productId: v.product_id,
        productName: v.product_name,
        variantId: v.variant_id,
        variantName: v.variant_name,
        qty: v.qty,
        omzet: v.revenue,
        hpp: v.cogs,
        labaKotor: v.gross_profit,
      })),
      addonRows: (dto.addon_rows ?? []).map((a) => ({
        outletId: a.outlet_id,
        outletName: a.outlet_name,
        addonId: a.addon_id,
        nama: a.name,
        qty: a.qty,
        omzet: a.revenue,
        hpp: a.cogs,
        labaKotor: a.gross_profit,
      })),
    }
  }

  async getCentralKitchen(
    sourceOutletId: string,
    from?: string,
    to?: string,
    destinationOutletId?: string,
  ): Promise<CentralKitchenReport> {
    const raw = await httpClient.get<unknown>('/reports/inventory/central-kitchen', {
      params: {
        source_outlet_id: sourceOutletId,
        from,
        to,
        destination_outlet_id: destinationOutletId,
      },
    })
    return unwrap<CentralKitchenReport>(raw)
  }
}
